<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Form, Field } from 'vee-validate'
import { ChevronDownIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import yup from '@/plugins/yup'
import { useI18n } from '@/composable/useI18n'
import { useCompanyDashboardStore } from '@/store/companyDashboard'
import { useErrorStore } from '@/store/error'
import ButtonLoader from '@/components/Globals/Feedbacks/ButtonLoader.vue'
import type { ICompanyDashboard } from '@/interfaces/entities/companyDashboard'
import {
  DASHBOARD_TYPE_VALUES,
  DEFAULT_DASHBOARD_TYPE,
  DashboardType,
} from '@/interfaces/enums/dashboardType'

const props = defineProps<{
  isOpen: boolean
  dashboard?: ICompanyDashboard | null
}>()

const emit = defineEmits<{
  close: []
  saved: []
}>()

const { t } = useI18n()
const store = useCompanyDashboardStore()
const errorStore = useErrorStore()

const isLoading = ref(false)
const formKey = ref(0)

const isEditMode = computed(() => props.dashboard != null)

const fieldLabelClass = 'mb-1.5 block text-xs font-bold uppercase tracking-wider text-body'
const fieldInputClass =
  'w-full border border-stroke bg-gray-2 px-3 py-2.5 text-sm text-black outline-none focus:border-primary'
const fieldErrorClass = 'mt-1 text-sm text-red'

const dashboardCreateSchema = computed(() =>
  yup.object().shape({
    name: yup
      .string()
      .required(t('errors.required', { label: t('custom_dashboards.create_modal.fields.name') }))
      .max(100, t('custom_dashboards.create_modal.errors.name_max')),
    description: yup
      .string()
      .max(500, t('custom_dashboards.create_modal.errors.description_max'))
      .optional(),
    dashboard_type: yup
      .string()
      .required(t('errors.required', { label: t('custom_dashboards.create_modal.fields.type') }))
      .oneOf(DASHBOARD_TYPE_VALUES),
  }),
)

const initialValues = computed(() => {
  if (props.dashboard) {
    return {
      name: props.dashboard.name,
      description: props.dashboard.description ?? '',
      dashboard_type: props.dashboard.dashboard_type as DashboardType,
    }
  }

  return {
    name: '',
    description: '',
    dashboard_type: DEFAULT_DASHBOARD_TYPE,
  }
})

const dashboardTypeOptions = computed(() =>
  DASHBOARD_TYPE_VALUES.map((value) => ({
    value,
    label: t(`custom_dashboards.types.${value}`),
  })),
)

function categoryDescription(type: DashboardType): string {
  return t(`custom_dashboards.create_modal.category_descriptions.${type}`)
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      formKey.value++
      errorStore.clearErrors()
    }
  },
)

async function onSubmit(values: {
  name: string
  description?: string
  dashboard_type: DashboardType
}) {
  isLoading.value = true
  try {
    const payload = {
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      dashboard_type: values.dashboard_type,
    }

    if (isEditMode.value && props.dashboard) {
      await store.updateDashboard(props.dashboard.id, payload)
    } else {
      await store.createDashboard(payload)
    }

    emit('saved')
    emit('close')
  } catch (error: unknown) {
    const apiErrors = (error as { response?: { data?: { errors?: string | string[] } } })?.response?.data?.errors
    const fallbackKey = isEditMode.value
      ? 'custom_dashboards.edit_modal.errors.generic'
      : 'custom_dashboards.create_modal.errors.generic'
    errorStore.setErrors(apiErrors ?? [t(fallbackKey)])
  } finally {
    isLoading.value = false
  }
}

function cancel() {
  emit('close')
}
</script>

<template>
  <div v-if="isOpen" class="fixed inset-0 z-[9999] flex items-center justify-center">
    <div class="absolute inset-0 bg-black/40" @click="cancel" />

    <div class="relative w-full max-w-xl overflow-hidden bg-white shadow-xl">
      <div class="h-1 bg-blue" />

      <div class="p-8">
        <button
          type="button"
          class="absolute right-6 top-7 text-gray-400 hover:text-black"
          @click="cancel"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>

        <h2 class="mb-6 text-xl font-bold text-black">
          {{ isEditMode ? t('custom_dashboards.edit_modal.title') : t('custom_dashboards.create_modal.title') }}
        </h2>

        <Form
          :key="formKey"
          :validation-schema="dashboardCreateSchema"
          :initial-values="initialValues"
          @submit="onSubmit"
          v-slot="{ values }"
        >
          <div class="space-y-5">
            <Field name="name" v-slot="{ field, errorMessage }">
              <div>
                <label :class="fieldLabelClass">
                  {{ t('custom_dashboards.create_modal.fields.name') }}
                </label>
                <input
                  v-bind="field"
                  type="text"
                  :class="fieldInputClass"
                  :placeholder="t('custom_dashboards.create_modal.placeholders.name')"
                />
                <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
              </div>
            </Field>

            <Field name="description" v-slot="{ field, errorMessage }">
              <div>
                <label :class="fieldLabelClass">
                  {{ t('custom_dashboards.create_modal.fields.description') }}
                </label>
                <textarea
                  v-bind="field"
                  rows="2"
                  :class="[fieldInputClass, 'resize-y']"
                  :placeholder="t('custom_dashboards.create_modal.placeholders.description')"
                />
                <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
              </div>
            </Field>

            <Field name="dashboard_type" v-slot="{ field, errorMessage }">
              <div>
                <label :class="fieldLabelClass">
                  {{ t('custom_dashboards.create_modal.fields.type') }}
                </label>
                <div class="relative">
                  <select
                    v-bind="field"
                    :class="[fieldInputClass, 'appearance-none pr-10 uppercase']"
                  >
                    <option
                      v-for="option in dashboardTypeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                  <ChevronDownIcon
                    class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  />
                </div>
                <p v-if="errorMessage" :class="fieldErrorClass">{{ errorMessage }}</p>
              </div>
            </Field>

            <div
              v-if="values.dashboard_type"
              class="border-l-4 border-blue bg-blue-50 px-4 py-3"
            >
              <p class="text-sm leading-relaxed text-body">
                {{ categoryDescription(values.dashboard_type as DashboardType) }}
              </p>
            </div>
          </div>

          <div class="mt-6 border-t border-stroke pt-6">
            <div class="flex justify-end gap-4">
              <button
                type="button"
                class="text-xs font-medium uppercase tracking-wide text-body hover:text-black disabled:opacity-50"
                :disabled="isLoading"
                @click="cancel"
              >
                {{ t('custom_dashboards.create_modal.actions.cancel') }}
              </button>
              <button
                type="submit"
                class="flex h-10 items-center justify-center bg-blue px-6 py-2.5 text-xs font-medium uppercase tracking-wide text-white hover:bg-blue-600 disabled:opacity-50"
                :disabled="isLoading"
              >
                <ButtonLoader v-if="isLoading" />
                <span v-else>
                  {{
                    isEditMode
                      ? t('custom_dashboards.edit_modal.actions.save')
                      : t('custom_dashboards.create_modal.actions.create')
                  }}
                </span>
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>